import { z } from 'zod'
import type { PipelineStage } from '../types/pipeline'

export const lessonSourceSchema = z.object({
  id: z.string(),
  originalPath: z.string(),
  displayName: z.string(),
  addedAt: z.string(),
  kind: z.enum(['pdf', 'video']).default('pdf'),
})

export const questionsSourceSchema = z.object({
  id: z.string(),
  originalPath: z.string(),
  displayName: z.string(),
  addedAt: z.string(),
})

export const providerConfigSchema = z.object({
  provider: z.enum(['anthropic', 'ollama', 'mlx']),
  model: z.string(),
  reasoning: z.enum(['minimal', 'low', 'medium', 'high', 'xhigh']).optional(),
})

// Enum zod degli stage overridabili da UI/manifest. Il `satisfies readonly
// PipelineStage[]` verifica SOLO che ogni entry qui sia un PipelineStage valido
// (subset check) — NON garantisce che tutti i PipelineStage siano coperti: TS
// non segnala stage mancanti (es. in passato mancavano silenziosamente
// `transcription`, `key-resolution`, `question-pool-generation`). Tenere
// allineato a mano con PipelineStage in ../types/pipeline.ts (17 stage reali +
// `question-pool-generation` + `seed-coverage` = 19 stage totali).
const PIPELINE_STAGES = [
  'transcription',
  'questions',
  'question-pool-generation',
  'source-index',
  'source-cards',
  'embeddings',
  'anchoring',
  'key-resolution',
  'assignment',
  'outline',
  'atom-classify',
  'chapters',
  'chapter-keypoints',
  'coverage',
  'seed-coverage',
  'concept-graph',
  'mega',
  'render',
  'bundle',
] as const satisfies readonly PipelineStage[]

const pipelineStageEnum = z.enum(PIPELINE_STAGES)

// `modelOverrides` è tollerante: chiavi persistite che non sono più uno stage
// valido (es. i vecchi stage fantasma `lesson-cards`/`evidence-packs`) vengono
// STRIPPATE con un warn invece di far fallire il parse dell'intero manifest —
// un manifest persistito con un override su uno stage rimosso deve restare
// leggibile (KISS, TASK-018 §3.2d correzione #4).
type PipelineStageKey = z.infer<typeof pipelineStageEnum>
type ProviderConfigValue = z.infer<typeof providerConfigSchema>

const modelOverridesSchema = z.record(z.string(), providerConfigSchema).transform((raw) => {
  const result: Partial<Record<PipelineStageKey, ProviderConfigValue>> = {}
  for (const [key, value] of Object.entries(raw)) {
    const parsedKey = pipelineStageEnum.safeParse(key)
    if (!parsedKey.success) {
      console.warn(
        `[schemas/project] modelOverrides: chiave stage sconosciuta "${key}" ignorata (stage rimosso o non valido).`,
      )
      continue
    }
    result[parsedKey.data] = value
  }
  return result
})

export const projectPreferencesSchema = z.object({
  presetOverride: z.enum(['cloud', 'hybrid', 'local', 'mlx']).nullable(),
  modelOverrides: modelOverridesSchema,
})

export const projectSourcesSchema = z.object({
  lessons: z.array(lessonSourceSchema),
  questions: questionsSourceSchema.optional(),
  reinforcingMaterials: z.array(lessonSourceSchema).optional(),
})

export const projectManifestSchema = z
  .object({
    version: z.literal(1),
    id: z.string(),
    name: z.string(),
    description: z.string().optional(),
    languageGuidance: z.string().optional(),
    createdAt: z.string(),
    updatedAt: z.string(),
    projectType: z.enum(['manual', 'exploratory']).default('manual'),
    seed: z.string().optional(),
    sources: projectSourcesSchema,
    preferences: projectPreferencesSchema,
  })
  .superRefine((data, ctx) => {
    if (data.projectType === 'exploratory') {
      if (!data.seed || data.seed.trim().length < 100) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['seed'],
          message:
            "Field 'seed' is required for exploratory projects and must be at least 100 characters",
        })
      }
    }
  })

export const projectSchema = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string().optional(),
  languageGuidance: z.string().optional(),
  createdAt: z.string(),
  updatedAt: z.string(),
  lastOpenedAt: z.string().optional(),
  folderPath: z.string(),
  sources: projectSourcesSchema,
  preferences: projectPreferencesSchema,
})
